import { Highlights, HighlightsItem, HighlightsTextItem } from '@cmg/design-system';
import { useIntl } from '@cmg/i18n';
import { FC, Fragment } from 'react';

import { getOfferingTypeLabel } from '../../../../common/util/offering/offeringTypeHelpers';
import { OfferingStatusChip } from '../../../../design-system/components/data-display/offering-status-chip/OfferingStatusChip';
import { OfferingStatus as OfferingStatusEnum, OfferingType, Sector } from '../../../../graphql';
import { sectorLabels } from '../../../constants';

type Props = {
  issuerName: string;
  isCreateNewOffering: boolean;
  sector?: Sector | null;
  sizeShares?: number | null;
  status?: OfferingStatusEnum;
};

/**
 * PrivatePlacementOfferingHighlights renders the offering header for private placements.
 * Pricing and last trade before items are omitted as private placements have no filings,
 * no follow-on pricing and no publicly traded instrument to price against. Size comes from
 * the private placement terms rather than the offering level sizeInShares, which is unset.
 */
export const PrivatePlacementOfferingHighlights: FC<Props> = ({
  issuerName,
  isCreateNewOffering,
  sector,
  sizeShares,
  status,
}) => {
  const intl = useIntl();

  return (
    <Highlights
      data-testid="offering-header-highlights"
      title={issuerName}
      titleItems={
        !isCreateNewOffering ? (
          <Fragment>
            <HighlightsTextItem
              data-testid="offering-type-highlight"
              name="OFFERING TYPE"
              text={getOfferingTypeLabel(OfferingType.PrivatePlacement)}
            />
            <HighlightsTextItem name="SECTOR" text={sector ? sectorLabels[sector] : '-'} />
            <HighlightsTextItem
              name="SIZE"
              text={sizeShares ? intl.formatNumber(sizeShares, { format: 'integer' }) : '-'}
            />
            {status && (
              <HighlightsItem
                name="OFFERING STATUS"
                value={<OfferingStatusChip status={status} />}
              />
            )}
          </Fragment>
        ) : null
      }
    />
  );
};
